import type { CustomerProfile } from '../customer-auth/types'
import { parseProfileAddressValue } from '../customer-auth/profileAddress'
import type { CheckoutAddressForm } from './flow'

export type CheckoutSavedAddress = {
  id: string
  label: string
  address: CheckoutAddressForm
  useShipping: boolean
  useBilling: boolean
  defaultShipping: boolean
  defaultBilling: boolean
}

const buildId = (prefix: string, value: string) => {
  const normalized = value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')
  return `${prefix}-${normalized.slice(0, 36) || 'address'}`
}

const parseProfileAddress = ({
  rawValue,
  profile,
}: {
  rawValue: string
  profile: CustomerProfile
}): CheckoutAddressForm | null => {
  const trimmed = rawValue.trim()
  if (!trimmed) return null

  const parsed = parseProfileAddressValue({
    value: trimmed,
    countryFallback: 'IT',
    phoneFallback: profile.phone,
  })
  if (!parsed) return null

  return {
    firstName: profile.firstName.trim(),
    lastName: profile.lastName.trim(),
    company: '',
    line1: parsed.addressLine,
    line2: '',
    city: parsed.city,
    postalCode: parsed.postalCode,
    region: parsed.province,
    country: parsed.country,
    phone: parsed.phone,
  }
}

export const buildCheckoutSavedAddresses = (profile: CustomerProfile | null): CheckoutSavedAddress[] => {
  if (!profile) return []

  // TODO(checkout): switch this adapter to dedicated backend address-book endpoints when available.
  const candidates: CheckoutSavedAddress[] = []

  const pushCandidate = ({
    key,
    label,
    rawValue,
    useShipping,
    useBilling,
    defaultShipping,
    defaultBilling,
  }: {
    key: string
    label: string
    rawValue: string
    useShipping: boolean
    useBilling: boolean
    defaultShipping: boolean
    defaultBilling: boolean
  }) => {
    const parsed = parseProfileAddress({ rawValue, profile })
    if (!parsed) return

    const signature = [parsed.line1, parsed.city, parsed.postalCode, parsed.country]
      .map((part) => part.trim().toLowerCase())
      .join('|')

    const existing = candidates.find((entry) =>
      [entry.address.line1, entry.address.city, entry.address.postalCode, entry.address.country]
        .map((part) => part.trim().toLowerCase())
        .join('|') === signature,
    )

    if (existing) {
      existing.useShipping = existing.useShipping || useShipping
      existing.useBilling = existing.useBilling || useBilling
      existing.defaultShipping = existing.defaultShipping || defaultShipping
      existing.defaultBilling = existing.defaultBilling || defaultBilling
      return
    }

    candidates.push({
      id: buildId(key, rawValue),
      label,
      address: parsed,
      useShipping,
      useBilling,
      defaultShipping,
      defaultBilling,
    })
  }

  pushCandidate({
    key: 'shipping',
    label: 'Shipping',
    rawValue: profile.shippingAddress,
    useShipping: true,
    useBilling: false,
    defaultShipping: true,
    defaultBilling: false,
  })

  pushCandidate({
    key: 'billing',
    label: 'Billing',
    rawValue: profile.billingAddress,
    useShipping: false,
    useBilling: true,
    defaultShipping: false,
    defaultBilling: true,
  })

  pushCandidate({
    key: 'residential',
    label: 'Residential',
    rawValue: profile.residentialAddress,
    useShipping: false,
    useBilling: false,
    defaultShipping: false,
    defaultBilling: false,
  })

  return candidates
}
